import { useTranslations } from 'next-intl';
import React, { useState } from 'react';

import { WebsiteInputStyle, WebsiteStyle } from '@cineamo/api/models/website-configuration/WebsiteStyle.types';

import { ButtonStyle } from '@cineamo/ui/src/button/ButtonStyle.enum';
import Button from '@cineamo/ui/src/button/NewButton';
import { IconLoading16 } from '@cineamo/ui/src/icons/ic-loading';
import Modal, { ModalProps } from '@cineamo/ui/src/modals/Modal';

export interface EmailInputModalProps extends Omit<ModalProps, 'onSubmit'> {
    onSubmit: (email: string) => void;
    isLoading?: boolean;
    websiteStyle?: WebsiteStyle & WebsiteInputStyle;
}

const EmailInputModal: React.FC<EmailInputModalProps> = ({
    onSubmit,
    isLoading = false,
    websiteStyle,
    ...modalProps
}) => {
    const t = useTranslations();
    const [email, setEmail] = useState('');
    const [isValidEmail, setIsValidEmail] = useState(false);

    const validateEmail = (email: string) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newEmail = e.target.value;
        setEmail(newEmail);
        setIsValidEmail(validateEmail(newEmail));
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (isValidEmail && !isLoading) {
            onSubmit(email);
        }
    };

    return (
        <Modal
            {...modalProps}
            className="h-min"
            title={t('tickets.payment.email-input.title')}
            onClose={isLoading ? undefined : modalProps.onClose}>
            <div
                style={
                    {
                        '--input-background-color':
                            websiteStyle?.inputBackgroundColorOnSurface || websiteStyle?.inputBackgroundColor,
                        '--input-text-color': websiteStyle?.inputTextColorOnSurface || websiteStyle?.inputTextColor,
                        '--input-placeholder-color':
                            websiteStyle?.inputPlaceholderColorOnSurface || websiteStyle?.inputPlaceholderColor
                    } as React.CSSProperties
                }>
                <form
                    onSubmit={handleSubmit}
                    className="flex flex-col space-y-24">
                    <div className="flex flex-col space-y-12">
                        <p className="text-body-14">{t('tickets.payment.email-input.explanation')}</p>

                        <div className="flex flex-col space-y-4">
                            <label
                                htmlFor="email-modal"
                                className="text-body-12 opacity-50">
                                {t('tickets.payment.email-input.email-address')}
                            </label>
                            <input
                                id="email-modal"
                                type="email"
                                className="w-full h-[48px] bg-[var(--input-background-color)] focus:outline-none focus:ring-2 focus:ring-inset focus:ring-[var(--focus-ring-color)] text-[var(--input-text-color)] text-body-16 rounded-[4px] px-[16px] border-none placeholder:text-[var(--input-placeholder-color)]"
                                placeholder="example@example.com"
                                value={email}
                                onChange={handleEmailChange}
                                required
                                disabled={isLoading}
                            />
                        </div>
                    </div>

                    <div className="flex justify-end space-x-12">
                        {!isLoading && (
                            <Button
                                type="button"
                                style={ButtonStyle.default}
                                onClick={modalProps.onClose ?? undefined}>
                                {t('common.back')}
                            </Button>
                        )}
                        <Button
                            type="submit"
                            style={ButtonStyle.default}
                            disabled={!isValidEmail || isLoading}
                            leadingIcon={isLoading && <IconLoading16 className="animate-spin" />}>
                            {isLoading ? t('tickets.loading') : t('tickets.continue')}
                        </Button>
                    </div>
                </form>
            </div>
        </Modal>
    );
};

export default EmailInputModal;
